import React from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

/**
 * Language selector — minimal, premium, international.
 *
 * No country flags (Arabic is a language, not a single country) and no
 * machine codes.  Each entry is rendered in its OWN native script so the
 * menu feels intentional and culturally natural rather than software-y.
 */
const LANGS = [
  { code: "da" },
  { code: "en" },
  { code: "es" },
  { code: "de" },
  { code: "fr" },
  { code: "ar" },
  { code: "it" },
  { code: "pt" },
  { code: "zh" },
  { code: "ja" },
];

const LanguageSelector = () => {
  const { t, i18n } = useTranslation();
  const current = LANGS.find((l) => l.code === i18n.resolvedLanguage) || LANGS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-selector-trigger"
        className="group flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-white/85 transition-all hover:border-[#6FF6FF]/60 hover:text-white hover:shadow-[0_0_24px_-6px_rgba(111,246,255,0.6)]"
      >
        <Globe className="h-3.5 w-3.5 text-[#6FF6FF]" />
        <span className="font-display text-[12.5px] tracking-[0.06em]">
          {t(`lang.${current.code}`)}
        </span>
        <ChevronDown className="h-3 w-3 opacity-70 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[200px] glass-strong border-white/10 p-1.5 text-white"
      >
        {LANGS.map((l) => {
          const active = i18n.resolvedLanguage === l.code;
          return (
            <DropdownMenuItem
              key={l.code}
              data-testid={`language-option-${l.code}`}
              onClick={() => i18n.changeLanguage(l.code)}
              className={`group/item cursor-pointer flex items-center justify-between gap-4 rounded-md px-3 py-2.5 transition-colors ${
                active
                  ? "bg-white/[0.04] text-[#6FF6FF]"
                  : "text-white/85 hover:text-white"
              } focus:bg-white/[0.05]`}
            >
              <span className="font-display text-[15px] leading-none">
                {t(`lang.${l.code}`)}
              </span>
              {active && <Check className="h-3.5 w-3.5 text-[#6FF6FF]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
